// utils
import { Box, css, keyframes, styled } from '@mui/material';

// styles
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ColumnWidth } from './Calendar.types';

export const blinkOpacity = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const CalendarResets = css({
  fontFamily: 'inherit',
  fontSize: 'inherit',
  color: 'inherit',
  border: 'none',
  padding: 0,
  margin: 0
});

export const CalendarContainer = styled(Box, {
  shouldForwardProp: (propName) => propName != 'columnWidths'
})<{
  columnWidths: ColumnWidth[];
}>(({ theme, columnWidths }) => ({
  display: 'flex',
  width: `calc(100% - ${theme.spacing(2)})`,
  padding: theme.spacing(1),

  '*': {
    userSelect: 'none'
  },

  // Resets and customization of imported calendar styling
  '.rbc-events-container, .rbc-day-slot, .rbc-event, .rbc-event.rbc-selected, .rbc-background-event, .rbc-background-event-selected':
    {
      ...CalendarResets
    },

  '.rbc-events-container': {
    // For dynamic showing/hiding of appointment event details (see CalendarAppointment styles)
    containerType: 'inline-size',
    pointerEvents: 'none'
  },
  '.rbc-day-slot .rbc-events-container': {
    // Create whitespace to the right of appointment events
    right: theme.spacing(3)
  },

  '.rbc-time-content, .rbc-timeslot-group': {
    borderColor: theme.vars.palette.surface.dim,

    '.rbc-time-slot': {
      borderColor: theme.vars.palette.surface.container
    }
  },
  '.rbc-time-content': {
    overflowY: 'hidden',
    marginTop: '5px'
  },
  '.entity-type-employee .time-column-DAY_EVENTS .rbc-day-slot': {
    backgroundColor: theme.palette.grey[200]
  },
  '.rbc-timeslot-group': {
    '.time-slot-cell-container': {
      display: 'flex',
      flex: '1',
      '.time-slot-cell': {
        flex: '1'
      }
    },
    '&.is-active': {
      '.time-slot-cell': {
        position: 'relative',
        flex: '1',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
          zIndex: 2,
          border: `1px solid ${theme.palette.primary.main}`,
          pointerEvents: 'none'
        }
      }
    },
    '&:not(.is-active) .time-slot-cell:hover': {
      cursor: 'pointer'
    }
  },
  '.rbc-time-gutter, .rbc-time-header-gutter': {
    minWidth: '60px',
    maxWidth: '60px',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,

    '.rbc-timeslot-group': {
      width: '100%',
      textAlign: 'center'
    }
  },
  '.rbc-time-header': {
    position: 'sticky',
    top: 60,
    zIndex: theme.zIndex.appBar,
    width: '100%',
    borderBottom: `2px solid ${theme.vars.palette.surface.dim}`,

    '.rbc-header': {
      backgroundColor: theme.palette.background.paper,

      '&.rbc-today': {
        backgroundColor: theme.vars.palette.surface.containerLow
      }
    },
    '.rbc-allday-cell': {
      backgroundColor: theme.palette.background.paper,

      '.rbc-day-bg + .rbc-day-bg': {
        borderLeftColor: theme.vars.palette.surface.dim
      }
    }
  },
  '.rbc-calendar': {
    width: '100%'
  },
  '.rbc-events-container, .rbc-event, .rbc-background-event': {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0
  },
  '.rbc-event-label': {
    display: 'none'
  },
  '.rbc-today': {
    backgroundColor: 'transparent'
  },
  '.rbc-time-view': {
    border: 'none',
    '.rbc-row': {
      minHeight: '38px'
    }
  },
  '.rbc-slot-selection': {
    fontSize: theme.typography.body1.fontSize,
    backgroundColor: theme.vars.palette.primary.main
  },
  '.rbc-current-time-indicator': {
    height: '2px',
    zIndex: 0,
    animation: `${blinkOpacity} 2s ease-in-out infinite`,
    backgroundColor: theme.vars.palette.primary.main
  },

  '.rbc-event, .rbc-background-event': {
    transition: 'box-shadow 100ms ease-in-out, border-color 100ms ease-in-out',
    pointerEvents: 'all'
  },

  '.rbc-background-event.not-available-bg-event ': {
    zIndex: 1,
    opacity: '0.5 !important'
  },

  '.time-column-DAY_EVENTS .rbc-background-event': {
    pointerEvents: 'none',
    width: `calc(100% + ${theme.spacing(3)}) !important`,
    borderRadius: 0,
    opacity: 1
  },
  '.time-column-DAY_EVENTS .rbc-timeslot-group': {
    zIndex: 1
  },
  '.time-column-DAY_EVENTS .rbc-event': {
    zIndex: 2
  },

  '.rbc-event:focus, .rbc-background-event:focus': {
    outline: 'none'
  },
  '.rbc-time-view-resources .rbc-day-slot, .rbc-resource-grouping .rbc-row .rbc-header, .rbc-time-view-resources .rbc-header, .rbc-time-view-resources .rbc-day-bg':
    {
      minWidth: 'unset',
      width: 'unset'
    },
  '.view-state-appointment': {
    '.resource-bundle': {
      ...Object.fromEntries(
        columnWidths.map((col) => [
          `&:nth-child(${col.idx} of .resource-bundle)`,
          {
            '.time-column-DAY_AVAILABILITIES': {
              display: col.columnWidth == 0 ? 'none' : 'default',
              width: col.columnWidth > 0 ? `${col.columnWidth * 8}px` : 0,
              paddingLeft: '1px'
            },
            '.time-column-DAY_EVENTS': {
              ...(col.columnWidth > 0 && {
                borderLeft: 'none',
                '.rbc-timeslot-group': {
                  position: 'relative',
                  left: `-${col.columnWidth * 6 + 1}px`,
                  width: `calc(100% + ${col.columnWidth * 6 + 1}px)`
                }
              })
            }
          }
        ])
      ),
      '.time-column-DAY_AVAILABILITIES': {
        // important is required to overrule the injected styling of RBC.
        flex: 'none !important',
        pointerEvents: 'none',
        '.rbc-day-slot': {
          minWidth: 'inherit'
        },
        '.rbc-event': {
          // important is required to overrule the injected styling of RBC.
          width: '6px !important',
          pointerEvents: 'none',
          zIndex: 2
        },
        '.availability-event': {
          pointerEvents: 'none'
        }
      }
    }
  },
  '.view-state-availability': {
    '.time-column-DAY_AVAILABILITIES': {
      display: 'default !important',
      width: '100% !important',
      paddingLeft: '1px',
      '.availability-event': {
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.7)',
          pointerEvents: 'none'
        }
      }
    },
    '.time-column-DAY_EVENTS': {
      display: 'none'
    }
  },
  '.rbc-time-header .rbc-time-header-content > .rbc-row': {
    '&:not(:first-of-type)': {
      display: 'none'
    },
    '.rbc-header': {
      borderBottom: 'none'
    }
  }
}));

export const CalendarRetryButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  height: 'calc(100vh - 220px)'
}));
